import { supabase } from '../lib/supabase';

interface CallLogRecord {
  id: string;
  sessionId: string;
  startTime: string;
  duration: number;
  type: string;
  direction: string;
  action: string;
  result: string;
  to: {
    phoneNumber: string;
    name?: string;
    extensionNumber?: string;
  };
  from: {
    phoneNumber: string;
    name?: string;
    extensionNumber?: string;
  };
  recording?: {
    id: string;
    uri: string;
    type: string;
    contentUri: string;
  };
  extension?: {
    id: string;
    extensionNumber: string;
  };
}

interface CallLogResponse {
  records: CallLogRecord[];
  paging: {
    page: number;
    perPage: number;
    pageStart: number;
    pageEnd: number;
  };
  navigation: {
    firstPage?: { uri: string };
    nextPage?: { uri: string };
    previousPage?: { uri: string };
    lastPage?: { uri: string };
  };
}

interface SMSMessage {
  to: string[];
  from?: string;
  text: string;
}

interface SMSResponse {
  id: string;
  uri: string;
  type: string;
  direction: string;
  messageStatus: string;
  creationTime: string;
  lastModifiedTime: string;
}

/**
 * Make an authenticated RingCentral API call through the server-side
 * 'ringcentral-proxy' edge function. The token exchange and the client
 * secret/JWT now live server-side; the browser never reads them. Supabase
 * auto-attaches the caller's auth header to the invoke.
 */
async function callRingCentralAPI(endpoint: string, options: RequestInit = {}): Promise<any> {
  const method = (options.method as string) || 'GET';
  const body = options.body ? JSON.parse(options.body as string) : undefined;

  const { data, error } = await supabase.functions.invoke('ringcentral-proxy', {
    body: { endpoint, method, body },
  });

  if (error) throw new Error(error.message || `RingCentral API error`);
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * Get call logs with optional filters
 */
export async function getCallLogs(params: {
  phoneNumber?: string;
  extensionNumber?: string;
  direction?: 'Inbound' | 'Outbound';
  type?: 'Voice' | 'Fax';
  dateFrom?: string; // ISO 8601 format
  dateTo?: string;
  page?: number;
  perPage?: number;
}): Promise<CallLogResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.phoneNumber) queryParams.append('phoneNumber', params.phoneNumber);
  if (params.extensionNumber) queryParams.append('extensionNumber', params.extensionNumber);
  if (params.direction) queryParams.append('direction', params.direction);
  if (params.type) queryParams.append('type', params.type);
  if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.append('dateTo', params.dateTo);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.perPage) queryParams.append('perPage', params.perPage.toString());
  
  queryParams.append('view', 'Detailed');

  const endpoint = `/account/~/call-log?${queryParams.toString()}`;
  return callRingCentralAPI(endpoint);
}

/**
 * Get call logs for a specific extension
 */
export async function getExtensionCallLogs(
  extensionId: string,
  params: {
    dateFrom?: string;
    dateTo?: string;
    direction?: 'Inbound' | 'Outbound';
    page?: number;
    perPage?: number;
  } = {}
): Promise<CallLogResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.append('dateTo', params.dateTo);
  if (params.direction) queryParams.append('direction', params.direction);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.perPage) queryParams.append('perPage', params.perPage.toString());
  
  queryParams.append('view', 'Detailed');

  const endpoint = `/account/~/extension/${extensionId}/call-log?${queryParams.toString()}`;
  return callRingCentralAPI(endpoint);
}

/**
 * Get call recording content
 */
export async function getCallRecording(recordingId: string): Promise<Blob> {
  const endpoint = `/account/~/recording/${recordingId}/content`;

  const { data, error } = await supabase.functions.invoke('ringcentral-proxy', {
    body: { endpoint, method: 'GET', binary: true },
  });

  if (error) throw new Error(error.message || `Failed to get recording`);
  if (data?.error) throw new Error(data.error);

  // The proxy returns the audio as base64 + content type; rebuild the Blob.
  const byteChars = atob(data.base64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  return new Blob([bytes], { type: data.contentType || 'audio/mpeg' });
}

/**
 * Send SMS message
 */
export async function sendSMS(message: SMSMessage): Promise<SMSResponse> {
  const endpoint = '/account/~/extension/~/sms';
  
  return callRingCentralAPI(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      to: message.to.map(phone => ({ phoneNumber: phone })),
      from: message.from ? { phoneNumber: message.from } : undefined,
      text: message.text,
    }),
  });
}

/**
 * Get SMS message history
 */
export async function getSMSHistory(params: {
  dateFrom?: string;
  dateTo?: string;
  phoneNumber?: string;
  page?: number;
  perPage?: number;
} = {}): Promise<any> {
  const queryParams = new URLSearchParams();
  
  if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.append('dateTo', params.dateTo);
  if (params.phoneNumber) queryParams.append('phoneNumber', params.phoneNumber);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.perPage) queryParams.append('perPage', params.perPage.toString());
  
  queryParams.append('messageType', 'SMS');

  const endpoint = `/account/~/extension/~/message-store?${queryParams.toString()}`;
  return callRingCentralAPI(endpoint);
}

/**
 * Make an outbound call (Click-to-Call)
 *
 * The RingCentral token exchange + RingOut request now run server-side in the
 * 'ringcentral-make-call' edge function so the client secret/JWT never reach the
 * browser. Supabase auto-attaches the caller's auth header to the invoke.
 */
export async function makeCall(params: {
  from: string; // Extension number or phone number
  to: string; // Phone number to call
}): Promise<any> {
  const { data, error } = await supabase.functions.invoke('ringcentral-make-call', {
    body: {
      from: params.from,
      to: params.to,
    },
  });

  if (error) throw new Error(error.message || 'Failed to initiate call');
  if (data?.error) throw new Error(data.error);

  return data;
}

/**
 * Get extension information
 */
export async function getExtension(extensionId: string): Promise<any> {
  const endpoint = `/account/~/extension/${extensionId}`;
  return callRingCentralAPI(endpoint);
}

/**
 * List all extensions
 */
export async function listExtensions(): Promise<any> {
  const endpoint = '/account/~/extension';
  return callRingCentralAPI(endpoint);
}

/**
 * Test RingCentral connection
 */
export async function testRingCentralConnection(): Promise<{ success: boolean; error?: string }> {
  try {
    // Token exchange happens server-side inside the proxy.
    await callRingCentralAPI('/account/~');
    return { success: true };
  } catch (error: any) {
    console.error('Error testing RingCentral connection:', error);
    return { success: false, error: error.message || 'Failed to connect to RingCentral API' };
  }
}

/**
 * Format phone number to E.164 format
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Add +1 for US numbers if not present
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }
  
  return phone;
}
